import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

import type { AnswerType, QuizVariant, ResultBucket } from 'components/BuildVsBuyQuiz/config/types';

interface QuizStore {
  // Core quiz data
  variant: QuizVariant;
  quizVersion: string;
  answers: Record<number, AnswerType>;
  email?: string;

  // Calculated results (auto-computed on every answer)
  blueCount: number;
  orangeCount: number;
  resultBucket?: ResultBucket;

  // Tracking flags (prevent duplicate events)
  hasTrackedStart: boolean;
  hasTrackedCompletion: boolean;

  // Actions
  setVariant: (variant: QuizVariant) => void;
  setQuizVersion: (version: string) => void;
  setAnswer: (questionIndex: number, answer: AnswerType) => void;
  clearAnswersFrom: (questionIndex: number) => void;
  setEmail: (email: string) => void;
  setResultBucket: (bucket: ResultBucket) => void;
  markStartTracked: () => void;
  markCompletionTracked: () => void;
  reset: () => void;
}

const initialState = {
  variant: 'gated_start' as QuizVariant,
  quizVersion: 'v1',
  answers: {},
  email: undefined,
  blueCount: 0,
  orangeCount: 0,
  resultBucket: undefined,
  hasTrackedStart: false,
  hasTrackedCompletion: false,
};

/**
 * Quiz Store with Zustand
 *
 * CRITICAL FEATURES:
 * - currentStep is CALCULATED from answers (not stored)
 * - Atomic score calculation on every answer
 * - Proper localStorage persistence with JSON storage
 * - Clean separation of data vs tracking flags
 *
 * Persisted fields:
 * - variant, quizVersion, answers, email
 * - blueCount, orangeCount, resultBucket
 *
 * NOT persisted (ephemeral):
 * - hasTrackedStart, hasTrackedCompletion
 * (These reset on page reload to allow re-tracking)
 *
 * Calculated (not stored):
 * - currentStep = Object.keys(answers).length + 1
 */
export const useQuizStore = create<QuizStore>()(
  persist(
    (set, _get) => ({
      ...initialState,

      setVariant: (variant: QuizVariant) => set({ variant }),

      setQuizVersion: (quizVersion: string) => set({ quizVersion }),

      /**
       * Set answer and automatically recalculate scores
       * This is the ONLY way to update answers, ensuring scores are always in sync
       */
      setAnswer: (questionIndex: number, answer: AnswerType) => {
        set(state => {
          const newAnswers = { ...state.answers, [questionIndex]: answer };

          // Recalculate scores immediately
          const blueCount = Object.values(newAnswers).filter(a => a === 'blue').length;
          const orangeCount = Object.values(newAnswers).filter(a => a === 'orange').length;

          return {
            answers: newAnswers,
            blueCount,
            orangeCount,
          };
        });
      },

      /**
       * Clear answers from specified question index onwards
       * Used for back button navigation
       */
      clearAnswersFrom: (questionIndex: number) => {
        set(state => {
          const newAnswers: Record<number, AnswerType> = {};

          // Keep only answers before this question
          Object.keys(state.answers).forEach(key => {
            const index = Number(key);
            if (index < questionIndex) {
              newAnswers[index] = state.answers[index];
            }
          });

          // Recalculate scores
          const blueCount = Object.values(newAnswers).filter(a => a === 'blue').length;
          const orangeCount = Object.values(newAnswers).filter(a => a === 'orange').length;

          return {
            answers: newAnswers,
            blueCount,
            orangeCount,
          };
        });
      },

      setEmail: (email: string) => set({ email }),

      setResultBucket: (resultBucket: ResultBucket) => set({ resultBucket }),

      markStartTracked: () => set({ hasTrackedStart: true }),

      markCompletionTracked: () => set({ hasTrackedCompletion: true }),

      /**
       * Complete reset to initial state
       * Used for fresh start or retake
       */
      reset: () => set(initialState),
    }),
    {
      name: 'build-vs-buy-quiz-storage',
      storage: createJSONStorage(() => localStorage),

      /**
       * Persist only essential data, not tracking flags
       * Tracking flags reset on reload to allow events to fire again
       */
      partialize: state => ({
        variant: state.variant,
        quizVersion: state.quizVersion,
        answers: state.answers,
        email: state.email,
        blueCount: state.blueCount,
        orangeCount: state.orangeCount,
        resultBucket: state.resultBucket,
        // hasTrackedStart and hasTrackedCompletion are NOT persisted
        // They reset on reload to allow tracking events to fire
      }),

      /**
       * Version for future migrations
       */
      version: 1,

      /**
       * CRITICAL: This callback fires AFTER hydration completes
       * Sets a sessionStorage flag to signal useQuizState that store is ready
       */
      onRehydrateStorage: () => () => {
        if (typeof window !== 'undefined') {
          sessionStorage.setItem('build-vs-buy-quiz-hydrated', 'true');
        }
      },
    },
  ),
);
